// Social authentication buttons for OAuth sign-in
// Supports Google, Facebook, and Apple OAuth providers
// Enable/disable providers via environment variables:
// - NEXT_PUBLIC_OAUTH_GOOGLE_ENABLED
// - NEXT_PUBLIC_OAUTH_FACEBOOK_ENABLED
// - NEXT_PUBLIC_OAUTH_APPLE_ENABLED

'use client';

import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/Facebook';
import AppleIcon from '@mui/icons-material/Apple';
import { useAuth, type OAuthProvider } from '@boty/database';
import { useTranslations, useLocale } from 'next-intl';
import { getSessionId } from '../../lib/session';

// Read enabled providers from environment variables
// Note: Must access env vars directly (not dynamically) for Next.js build-time inlining
const ENV_GOOGLE_ENABLED = process.env.NEXT_PUBLIC_OAUTH_GOOGLE_ENABLED === 'true';
const ENV_FACEBOOK_ENABLED = process.env.NEXT_PUBLIC_OAUTH_FACEBOOK_ENABLED === 'true';
const ENV_APPLE_ENABLED = process.env.NEXT_PUBLIC_OAUTH_APPLE_ENABLED === 'true';

export interface SocialAuthButtonsProps {
  /** Text to display in the divider below buttons (optional) */
  dividerText?: string;
  /** Called when OAuth redirect is about to happen */
  onOAuthStart?: (provider: OAuthProvider) => void;
  /** Override enabled providers (for testing). Defaults to reading from env vars. */
  enabledProviders?: OAuthProvider[];
}

/**
 * SocialAuthButtons component displays OAuth sign-in buttons for Google, Facebook, and Apple.
 * Handles loading states and errors, and preserves session ID for bot linking.
 *
 * Enable providers via environment variables:
 * - NEXT_PUBLIC_OAUTH_GOOGLE_ENABLED=true
 * - NEXT_PUBLIC_OAUTH_FACEBOOK_ENABLED=true
 * - NEXT_PUBLIC_OAUTH_APPLE_ENABLED=true
 */
export function SocialAuthButtons({
  dividerText,
  onOAuthStart,
  enabledProviders,
}: SocialAuthButtonsProps) {
  const { signInWithOAuth } = useAuth();
  const t = useTranslations('creator.auth');
  const locale = useLocale();
  const [loadingProvider, setLoadingProvider] = useState<OAuthProvider | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  // Determine which providers are enabled (prop override or env vars)
  const googleEnabled = enabledProviders
    ? enabledProviders.includes('google')
    : ENV_GOOGLE_ENABLED;
  const facebookEnabled = enabledProviders
    ? enabledProviders.includes('facebook')
    : ENV_FACEBOOK_ENABLED;
  const appleEnabled = enabledProviders
    ? enabledProviders.includes('apple')
    : ENV_APPLE_ENABLED;

  // Check if any providers are enabled
  const hasEnabledProviders = googleEnabled || facebookEnabled || appleEnabled;

  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    setError(null);
    setLoadingProvider(provider);
    onOAuthStart?.(provider);

    // Get session ID to pass through OAuth flow for bot linking
    const sessionId = getSessionId();

    // Build redirect URL with locale and session ID
    const callbackUrl = new URL('/auth/callback', window.location.origin);
    callbackUrl.searchParams.set('locale', locale);
    if (sessionId) {
      callbackUrl.searchParams.set('session_id', sessionId);
    }

    const { error: authError } = await signInWithOAuth({
      provider,
      redirectTo: callbackUrl.toString(),
    });

    if (authError) {
      console.error('[SocialAuth] OAuth error:', authError);
      setError(t('socialAuthError'));
      setLoadingProvider(null);
    }
    // Note: Success redirects to OAuth provider, so no further action needed
  };

  // Don't render anything if no providers are enabled
  if (!hasEnabledProviders) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
          slotProps={{ closeButton: { 'aria-label': 'close' } }}
        >
          {error}
        </Alert>
      )}

      {/* Google */}
      {googleEnabled && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={
            loadingProvider === 'google' ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <GoogleIcon />
            )
          }
          onClick={() => handleOAuthSignIn('google')}
          disabled={!!loadingProvider}
          sx={{ mb: 1.5 }}
          data-testid="google-oauth-button"
        >
          {t('continueWithGoogle')}
        </Button>
      )}

      {/* Facebook */}
      {facebookEnabled && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={
            loadingProvider === 'facebook' ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <FacebookIcon />
            )
          }
          onClick={() => handleOAuthSignIn('facebook')}
          disabled={!!loadingProvider}
          sx={{
            mb: 1.5,
            color: '#1877F2',
            borderColor: '#1877F2',
            '&:hover': {
              borderColor: '#1877F2',
              backgroundColor: 'rgba(24, 119, 242, 0.04)',
            },
          }}
          data-testid="facebook-oauth-button"
        >
          {t('continueWithFacebook')}
        </Button>
      )}

      {/* Apple */}
      {appleEnabled && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={
            loadingProvider === 'apple' ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <AppleIcon />
            )
          }
          onClick={() => handleOAuthSignIn('apple')}
          disabled={!!loadingProvider}
          sx={{
            mb: 2,
            bgcolor: '#000',
            color: '#fff',
            borderColor: '#000',
            '&:hover': {
              bgcolor: '#333',
              borderColor: '#333',
            },
            '&.Mui-disabled': {
              bgcolor: '#666',
              color: '#ccc',
              borderColor: '#666',
            },
          }}
          data-testid="apple-oauth-button"
        >
          {t('continueWithApple')}
        </Button>
      )}

      {dividerText && (
        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {dividerText}
          </Typography>
        </Divider>
      )}
    </Box>
  );
}
